/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  ButtonGroup,
  Typography,
  Alert
} from '@one-thd/sui-atomic-components';
import { AddToContext } from '../../AddToContext';
import {
  ITEMS_CANNOT_BE_QUOTED_MESSAGE,
  ITEMS_QUOTED_BY_PRO_ASSOCIATE_MESSAGE,
  ADDITIONAL_HANDLING_MESSAGE,
  ADDITIONAL_ASSISTANCE_MESSAGE,
  DISCONTINUED_OOS_MESSAGE,
  UNAVAILABLE_MESSAGE
} from '../../AddToConstants';
import { getProductsByType } from '../../AddToHelpers';
import { ExceptionsGroup } from './ExceptionsGroup';
import { ExceptionsType } from './ExceptionsType';
import { CopyExceptions } from './CopyExceptions';

export const ProductExceptions = ({
  handleOpenATQ, productExceptions, showATQ
}) => {
  const { handleClose } = useContext(AddToContext);

  const {
    qcProducts = [],
    nonSharedQcProducts = [],
    majorAppliances = [],
    configurableBlinds = [],
    liveGoods = [],
    discontinuedOutOfStockUnavailable = []
  } = getProductsByType(productExceptions);

  const discontinuedOutOfStock = discontinuedOutOfStockUnavailable.filter((product) => product?.identifiers != null);
  const unavailable = discontinuedOutOfStockUnavailable.filter((product) => product?.identifiers == null);

  const numberOfProductExceptions = productExceptions?.length;

  const handleContinueButtonClick = () => {
    if (showATQ) {
      handleOpenATQ();
    }
    handleClose();
  };

  const exceptionSections = [
    {
      condition: qcProducts.length > 0 || nonSharedQcProducts.length > 0,
      title: 'Special Order Items',
      message: ADDITIONAL_HANDLING_MESSAGE,
      children: (
        <>
          {qcProducts.length > 0 && <ExceptionsGroup productsType={qcProducts} />}
          {nonSharedQcProducts.length > 0 && (
            <ExceptionsGroup productsType={nonSharedQcProducts} isNonSharedQcProducts />
          )}
        </>
      )
    },
    {
      condition: majorAppliances.length > 0,
      title: 'Major Appliances',
      message: ADDITIONAL_ASSISTANCE_MESSAGE,
      products: majorAppliances
    },
    {
      condition: configurableBlinds.length > 0,
      title: 'Configurable Blinds',
      message: ADDITIONAL_ASSISTANCE_MESSAGE,
      products: configurableBlinds
    },
    {
      condition: liveGoods.length > 0,
      title: 'Live Goods',
      message: ADDITIONAL_ASSISTANCE_MESSAGE,
      products: liveGoods
    },
    {
      condition: discontinuedOutOfStock.length > 0,
      title: 'Discontinued / Out of Stock',
      message: DISCONTINUED_OOS_MESSAGE,
      products: discontinuedOutOfStock
    },
    {
      condition: unavailable.length > 0,
      title: 'Unavailable',
      message: `${unavailable?.length} ${UNAVAILABLE_MESSAGE}`,
      skipGroup: true
    }
  ];

  const renderExceptionSection = (exceptionSection) => {
    if (!exceptionSection.condition) {
      return null;
    }

    return (
      <div key={exceptionSection.title}>
        <ExceptionsType itemType={exceptionSection.title} message={exceptionSection.message} />
        {!exceptionSection.skipGroup && (
          exceptionSection.children
            ? exceptionSection.children
            : exceptionSection.products && <ExceptionsGroup productsType={exceptionSection.products} />
        )}
      </div>
    );
  };

  return (
    <>
      <DrawerBody>
        <div className="sui-min-h-screen">
          <Alert status="warning">
            <div className="sui-flex sui-flex-col sui-gap-4">
              <Typography>
                {numberOfProductExceptions} {ITEMS_CANNOT_BE_QUOTED_MESSAGE}
              </Typography>
              <Typography>
                {ITEMS_QUOTED_BY_PRO_ASSOCIATE_MESSAGE}
              </Typography>
            </div>
          </Alert>
          <div className="sui-mt-2 sui-mb-6">
            <div className="sui-flex sui-flex-row sui-gap-2">
              <CopyExceptions productExceptions={productExceptions} />
            </div>
          </div>
          <div className="sui-flex sui-flex-col sui-gap-4">
            <Typography variant="body-lg" weight="bold">
              Items Requiring Assistance
            </Typography>
            {exceptionSections.map(renderExceptionSection)}
          </div>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup orientation="vertical">
          <Button
            fullWidth
            onClick={handleContinueButtonClick}
            variant="primary"
          >
            Continue
          </Button>
          <Button
            fullWidth
            onClick={handleClose}
            variant="secondary"
          >
            Cancel
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
};

ProductExceptions.displayName = 'ProductExceptions';

ProductExceptions.propTypes = {
  handleOpenATQ: PropTypes.func.isRequired,
  productExceptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  showATQ: PropTypes.bool.isRequired
};